export type MlKemVariant = 'ml-kem-512' | 'ml-kem-768' | 'ml-kem-1024';
export type MlDsaVariant = 'ml-dsa-44' | 'ml-dsa-65' | 'ml-dsa-87';
export type SlhDsaVariant =
  | 'slh-dsa-sha2-128f'
  | 'slh-dsa-sha2-192f'
  | 'slh-dsa-sha2-256f';

type WorkerRequest = {
  id: number;
  method: string;
  params?: unknown;
};

type WorkerResponse =
  | {
      id: number;
      ok: true;
      result: unknown;
    }
  | {
      id: number;
      ok: false;
      error: string;
    };

type PendingRequest = {
  resolve: (value: unknown) => void;
  reject: (reason?: unknown) => void;
};

const WORKER_URL = '/scripts/tools/pqc-worker.js';

class PqcEngine {
  private worker: Worker;
  private pending = new Map<number, PendingRequest>();
  private nextId = 1;

  constructor() {
    if (typeof Worker === 'undefined') {
      throw new Error('Web Workers are not available in this environment.');
    }
    this.worker = new Worker(WORKER_URL, { type: 'module' });
    this.worker.addEventListener('message', (event) => this.handleMessage(event));
    this.worker.addEventListener('error', (event) => this.handleError(event));
  }

  private handleMessage(event: MessageEvent<WorkerResponse>) {
    const message = event.data;
    const pending = this.pending.get(message.id);
    if (!pending) return;
    this.pending.delete(message.id);
    if (message.ok) {
      pending.resolve(message.result);
    } else {
      pending.reject(new Error(message.error));
    }
  }

  private handleError(event: ErrorEvent) {
    const error = event.error ?? new Error(event.message);
    this.pending.forEach((pending) => pending.reject(error));
    this.pending.clear();
  }

  private call<T>(method: string, params?: unknown): Promise<T> {
    const id = this.nextId++;
    return new Promise((resolve, reject) => {
      this.pending.set(id, { resolve, reject });
      const payload: WorkerRequest = { id, method, params };
      this.worker.postMessage(payload);
    }) as Promise<T>;
  }

  mlKemKeygen(variant: MlKemVariant) {
    return this.call<{ publicKey: Uint8Array; secretKey: Uint8Array }>('mlKemKeygen', {
      variant,
    });
  }

  mlKemEncapsulate(variant: MlKemVariant, publicKey: Uint8Array) {
    return this.call<{ cipherText: Uint8Array; sharedSecret: Uint8Array }>(
      'mlKemEncapsulate',
      {
        variant,
        publicKey,
      }
    );
  }

  mlKemDecapsulate(variant: MlKemVariant, secretKey: Uint8Array, cipherText: Uint8Array) {
    return this.call<{ sharedSecret: Uint8Array }>('mlKemDecapsulate', {
      variant,
      secretKey,
      cipherText,
    });
  }

  mlDsaKeygen(variant: MlDsaVariant) {
    return this.call<{ publicKey: Uint8Array; secretKey: Uint8Array }>('mlDsaKeygen', {
      variant,
    });
  }

  mlDsaSign(variant: MlDsaVariant, secretKey: Uint8Array, message: Uint8Array) {
    return this.call<{ signature: Uint8Array }>('mlDsaSign', {
      variant,
      secretKey,
      message,
    });
  }

  mlDsaVerify(
    variant: MlDsaVariant,
    publicKey: Uint8Array,
    message: Uint8Array,
    signature: Uint8Array
  ) {
    return this.call<{ valid: boolean }>('mlDsaVerify', {
      variant,
      publicKey,
      message,
      signature,
    });
  }

  slhDsaKeygen(variant: SlhDsaVariant) {
    return this.call<{ publicKey: Uint8Array; secretKey: Uint8Array }>('slhDsaKeygen', {
      variant,
    });
  }

  slhDsaSign(variant: SlhDsaVariant, secretKey: Uint8Array, message: Uint8Array) {
    return this.call<{ signature: Uint8Array }>('slhDsaSign', {
      variant,
      secretKey,
      message,
    });
  }

  slhDsaVerify(
    variant: SlhDsaVariant,
    publicKey: Uint8Array,
    message: Uint8Array,
    signature: Uint8Array
  ) {
    return this.call<{ valid: boolean }>('slhDsaVerify', {
      variant,
      publicKey,
      message,
      signature,
    });
  }
}

let enginePromise: Promise<PqcEngine> | null = null;

export function getPqcEngine(): Promise<PqcEngine> {
  if (!enginePromise) {
    enginePromise = Promise.resolve(new PqcEngine());
  }
  return enginePromise;
}
